import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authAPI } from '../lib/api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  const checkAuth = useCallback(async () => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }

    // Skip auth check on public pages to avoid 401 errors
    const publicPaths = ['/', '/login', '/register'];
    const currentPath = window.location.pathname;
    const isPublicPage = publicPaths.includes(currentPath) || currentPath.startsWith('/p/') || currentPath.startsWith('/shop') || currentPath === '/manual';

    const token = localStorage.getItem('signa_token');
    const savedUser = localStorage.getItem('signa_user');

    if (token && savedUser) {
      try {
        const response = await authAPI.getMe();
        setUser(response.data);
        setIsAuthenticated(true);
      } catch (error) {
        // Token expired or invalid
        localStorage.removeItem('signa_token');
        localStorage.removeItem('signa_user');
        setUser(null);
        setIsAuthenticated(false);
      }
    } else if (!isPublicPage) {
      // Only try cookie-based auth on protected pages
      try {
        const response = await authAPI.getMe();
        setUser(response.data);
        setIsAuthenticated(true);
      } catch {
        setUser(null);
        setIsAuthenticated(false);
      }
    } else {
      setUser(null);
      setIsAuthenticated(false);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const response = await authAPI.login({ email, password });
    const { user: userData, token } = response.data;
    localStorage.setItem('signa_token', token);
    localStorage.setItem('signa_user', JSON.stringify(userData));
    setUser(userData);
    setIsAuthenticated(true);
    return userData;
  };

  const register = async (name, email, password, role = 'employee') => {
    const response = await authAPI.register({ name, email, password, role });
    const { user: userData, token } = response.data;
    localStorage.setItem('signa_token', token);
    localStorage.setItem('signa_user', JSON.stringify(userData));
    setUser(userData);
    setIsAuthenticated(true);
    return userData;
  };

  const loginWithOAuth = async (sessionId) => {
    const response = await authAPI.exchangeSession(sessionId);
    const userData = response.data;
    // Session token is set via cookie by backend
    localStorage.setItem('signa_user', JSON.stringify(userData));
    setUser(userData);
    setIsAuthenticated(true);
    return userData;
  };

  const logout = async () => {
    try {
      await authAPI.logout();
    } catch {
      // Ignore logout errors
    }
    localStorage.removeItem('signa_token');
    localStorage.removeItem('signa_user');
    setUser(null);
    setIsAuthenticated(false);
  };

  const value = {
    user,
    loading,
    isAuthenticated,
    login,
    register,
    loginWithOAuth,
    logout,
    checkAuth,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
